import React, { useEffect, useState } from "react";
import {
  Box, Typography, CircularProgress, Accordion, AccordionSummary,
  AccordionDetails, Chip, Divider,
} from "@mui/material";
import { ExpandMore, Group, DevicesOther } from "@mui/icons-material";
import { getMyDeviceGroupListForForm } from "../services/deviceGroupService";

interface DeviceItem {
  id: number;
  deviceName: string;
}

interface MyGroupDto {
  id: number;
  name: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  devices: DeviceItem[];
}

const MyDeviceGroups: React.FC = () => {
  const [groups, setGroups] = useState<MyGroupDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getMyDeviceGroupListForForm();
        setGroups((res.data as any).data ?? []);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 8, gap: 2 }}>
        <Group sx={{ fontSize: 64, color: "text.disabled" }} />
        <Typography color="text.secondary">Atanmış cihaz grubunuz bulunmamaktadır.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={3}>
        Cihaz Gruplarım
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {groups.map((group) => (
          <Accordion key={group.id} disableGutters elevation={1} sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                <Group color="primary" />
                <Typography fontWeight={500}>{group.name}</Typography>
                <Chip
                  label={`${group.devices.length} cihaz`}
                  size="small"
                  sx={{ ml: "auto", mr: 1 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Divider sx={{ mb: 1.5 }} />
              {group.devices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Bu grupta cihaz bulunmamaktadır.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {group.devices.map((device) => (
                    <Box
                      key={device.id}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 1.5, py: 1, borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    >
                      <DevicesOther fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={500}>{device.deviceName}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default MyDeviceGroups;
